import { PropsWithChildren } from 'react';
import { useRouter } from 'next/router';
import { getTimeAgoString } from '@/presentation/services/time/getTimeAgo';
import { getMyDataUrl } from '@/presentation/services/urls/profile';
import { useEditNicknameModal } from './components/EditNicknameModal';
import { ProfileUser } from '../../../../domain/entities/User';
import useCommandHandlers from './hooks/useCommandHandler';
import PageWrapper from './components/PageWrapper';
import { getTotalCommentReactionsFromUser } from './services/getTotalCommentReactionsFromProfile';

type ProfilePageContainerProps = PropsWithChildren<{
  userData: Omit<ProfileUser, 'offers' | 'comments'>;
  isLogged?: boolean;
  isOwner?: boolean;
  updateNickname?: (
    value: string
  ) => Promise<{ success: boolean; error?: string }>;
  onUpdateAvatar?: ({ file }: { file: File }) => void;
  isFollowing?: boolean;
  onFollow?: (params: { userId: string }) => void;
  onUnfollow?: (params: { userId: string }) => void;
}>;

function ProfilePageContainer({
  userData,
  isLogged,
  isOwner,
  isFollowing,
  updateNickname,
  onUpdateAvatar,
  onFollow,
  onUnfollow,
  children,
}: ProfilePageContainerProps) {
  const { locale, push } = useRouter();
  const nickname = userData?.nickname;

  const { showUpdateNicknameModal } = useEditNicknameModal({
    defaultValue: nickname,
    updateNickname,
  });

  const showMyDataPage = () => {
    push(getMyDataUrl(locale));
  };

  const commandHandler = useCommandHandlers({
    isOwner,
    nickname,
    onUpdateAvatar,
    showUpdateNicknameModal,
    followUserHandler: onFollow,
    unfollowUserHandler: onUnfollow,
    showMyDataPage,
  });

  return (
    <PageWrapper
      command={commandHandler}
      isFollowing={isFollowing}
      userData={{
        isLogged,
        isOwner,
        isAdmin: userData.isAdmin,
        avatar: userData.image?.url || '',
        id: userData.id,
        likes: userData.totalCommentLikes,
        totalDeals: userData.totalOffers || 0,
        maxTemperature: Math.round(userData.maxTemperature),
        memberSince: getTimeAgoString(
          new Date(userData.createdAt),
          locale || ''
        ),
        nickname: userData.nickname,
        totalFollowedUsers: userData.totalFollowedUsers,
        totalUsersFollowing: userData.totalUsersFollowing,
        isVerified: userData.isVerified,
        totalCommentReactions: getTotalCommentReactionsFromUser(
          userData.totalCommentReactions
        ),
      }}
    >
      {children}
    </PageWrapper>
  );
}

export default ProfilePageContainer;
